/**
 * Hello friend! APIActionResults is an observer for all requests that go
 * through the page.
 */
import type {ItemStatus} from "@khan/content-types";

import type {
    ActionResults,
    ContentItemCompletionStatus,
    GraphQLActionResults,
} from "./types.ts";

const APIActionResults = {
    hooks: [] as Array<{
        prop: string;
        fxn: (arg1?: any) => unknown;
    }>,

    /**
     * Converts our Python code's underscore_variable_notation to camelCase
     *
     * TODO: can remove when all of our API calls use casing:camel, see
     * APIActionResults.register above.
     */
    toCamelCase: function (prop: string): string {
        // From http://stackoverflow.com/questions/6660977/convert-hyphens-to-camel-case-camelcase
        return prop.replace(/_([a-z])/g, function (match, ch) {
            return ch.toUpperCase();
        });
    },

    convertItemStatus: function (
        gqlStatus: ContentItemCompletionStatus,
    ): ItemStatus {
        switch (gqlStatus) {
            case "STARTED":
                return "started";
            case "COMPLETE":
                return "complete";
            case "UNSTARTED":
            default:
                return "unstarted";
        }
    },

    /**
     * Munge the ActionResults from GraphQL to match the original format.
     */
    fromGraphQL: function (action: GraphQLActionResults): ActionResults {
        const notes = {
            avatarParts: [],
            badges: [],
            readable: [],
            toast: [],
            urgent: [],
            // null doesn't work here but the effect should be the same
            continueUrl: undefined,
            ...action.notificationsAdded,
        } as const;

        // TODO(LP-10194): Post-Goliath, only the Toast notifications field
        // will be populated, which will contain assignment completed
        // notifications. ContinueURL is always an empty string and the
        // other notification types are all empty lists.
        const notificationsAdded = {
            avatarParts: notes.avatarParts.map((v) => JSON.parse(v)),
            badges: notes.badges.map((v) => JSON.parse(v)),
            readable: notes.readable.map((v) => JSON.parse(v)),
            toast: notes.toast.map((v) => JSON.parse(v)),
            urgent: notes.urgent.map((v) => JSON.parse(v)),
            continueUrl: notes.continueUrl,
        } as const;

        const tutorialNodeProgress =
            action.tutorialNodeProgress &&
            action.tutorialNodeProgress.map(({contentId, progress}) => ({
                contentId,
                progress: APIActionResults.convertItemStatus(progress),
            }));

        const {pointsEarned, userProfile} = action;

        // NOTE(jvoll): TypeScript does not do well with objects with optional fields
        // (GraphQLActionResults) and spreading. This was previously
        // implemented with a spread of `action` and overrides on some fields
        // but TypeScript failed to detect that `tutorialNodeProgress` could be
        // completely missing despite `ActionResults` requiring it. As a
        // result, I've made this return type more explicit for now.
        return {
            notificationsAdded,
            tutorialNodeProgress: tutorialNodeProgress || [],
            // @ts-expect-error - TS2339 - Property 'currentTask' does not exist on type 'GraphQLActionResults'. | TS2339 - Property 'currentTask' does not exist on type 'GraphQLActionResults'.
            ...(action.currentTask ? {currentTask: action.currentTask} : null),
            ...((userProfile ? {userProfile} : null) as any),
            ...(pointsEarned ? {pointsEarned} : null),
        };
    },

    respondToAction: function (action: ActionResults) {
        APIActionResults.hooks.forEach(function (el, ix) {
            if (typeof action[el.prop] !== "undefined") {
                el.fxn(action[el.prop]);
            }
        });
    },

    respondToGraphQLAction: function (action: GraphQLActionResults) {
        APIActionResults.respondToAction(APIActionResults.fromGraphQL(action));
    },

    /**
     * Register both prop and the camelCase version of prop as an API event
     * listener.
     *
     * TODO: when all of our API calls use casing:camel, we won't need
     * toCamelCase because everything will register with APIActionResults using
     * the camelCased variable name.
     */
    register: function (
        this: any,
        prop: string,
        fxn: (arg1?: any) => unknown,
    ): () => void {
        const hooks = [
            {prop: prop, fxn: fxn},
            {prop: APIActionResults.toCamelCase(prop), fxn: fxn},
        ];
        this.hooks.push(...hooks);
        return () => {
            for (const hook of hooks) {
                const index = this.hooks.indexOf(hook);
                if (index !== -1) {
                    this.hooks.splice(index, 1);
                }
            }
        };
    },
} as const;

export default APIActionResults;
